/**
 * M.2 — NIPE tool registry barrel.
 *
 * Single source of truth for the list of tools NIPE may invoke during
 * a chat. `toolRunner.ts` dispatches to handlers via this registry.
 * The transport-level `serializeTools()` and `SKILL_TOOLS` exports in
 * `../toolSchemas.ts` re-export from here.
 *
 * Per-tool files each export `{schema, handler, mutatesRemote, requiresApproval}`.
 *
 * @see ./README.md for the catalog with surface effects + approval flags.
 */

import { applySkillTool, type ApplySkillContext } from './applySkill';
import { getSkillTool, type GetSkillContext } from './getSkill';
import {
  listSharePointDocsTool,
  type ListSharePointDocsContext,
} from './listSharePointDocs';
import { listSkillsTool, type ListSkillsContext } from './listSkills';
import { openVaultNoteTool, type OpenVaultNoteContext } from './openVaultNote';
import {
  proposeSharePointDocumentTool,
  type ProposeSharePointDocumentContext,
} from './proposeSharePointDocument';
import { searchVaultTool, type SearchVaultContext } from './searchVault';
// F18 — Customer onboarding tools (5 tools, NIPE-augmented self-serve wizard)
import {
  openOnboardWizardTool,
  type OpenOnboardWizardContext,
} from './openOnboardWizard';
import {
  setFormFieldTool,
  type SetFormFieldContext,
} from './setFormField';
import {
  inspectTenantTool,
  type InspectTenantContext,
} from './inspectTenant';
import {
  kickAdminConsentTool,
  type KickAdminConsentContext,
} from './kickAdminConsent';
import {
  submitWizardStepTool,
  type SubmitWizardStepContext,
} from './submitWizardStep';
// L.4 — Specialization Lab discovery tools
import { explainLoraTool, type ExplainLoraContext } from './explainLora';
import { recommendCorpusTool, type RecommendCorpusContext } from './recommendCorpus';
// L.15 — Tinker hyperparameter knowledge + Lab navigation
import {
  openSpecializationLabTool,
  type OpenSpecializationLabContext,
} from './openSpecializationLab';
import {
  describeSpecializationControlsTool,
  type DescribeSpecializationControlsContext,
} from './describeSpecializationControls';
// Generic openWindow — covers all registered desktop windows so NIPE
// can drive surfaces the bespoke per-window tools don't cover.
import { openWindowTool, type OpenWindowContext } from './openWindow';
// F-NIPE-DESKTOP-AWARENESS — window-control siblings to openWindow.
import { closeWindowTool, type CloseWindowContext } from './closeWindow';
import { focusWindowTool, type FocusWindowContext } from './focusWindow';
import {
  minimizeWindowTool,
  type MinimizeWindowContext,
} from './minimizeWindow';
import {
  listOpenWindowsTool,
  type ListOpenWindowsContext,
} from './listOpenWindows';
import {
  readWindowContextTool,
  type ReadWindowContextContext,
} from './readWindowContext';
// F-NIPE-PROMPT-LAB-DRIVER — bespoke read + mutate tools for the
// active Prompt Lab session. The bridge surfaces metadata; these
// tools carry the body and can rewrite it (gated by approval).
import {
  readPromptSessionTool,
  type ReadPromptSessionContext,
} from './readPromptSession';
import {
  setSystemPromptTool,
  type SetSystemPromptContext,
} from './setSystemPrompt';
import {
  setUserPromptTool,
  type SetUserPromptContext,
} from './setUserPrompt';
// F-NIPE-PROMPT-LAB-SKILLS — add / remove / update skill prompts on
// the active Prompt Lab session. All approval-gated; dispatch through
// existing Redux actions in @repancy/core.
import {
  addSkillPromptTool,
  type AddSkillPromptContext,
} from './addSkillPrompt';
import {
  removeSkillPromptTool,
  type RemoveSkillPromptContext,
} from './removeSkillPrompt';
import {
  updateSkillPromptTool,
  type UpdateSkillPromptContext,
} from './updateSkillPrompt';
import type { NipeTool, NipeToolSchema } from './types';

export type { NipeTool, NipeToolSchema, NipeToolCall, NipeToolResult, NipeToolHandler } from './types';
export { listSkillsTool } from './listSkills';
export { getSkillTool } from './getSkill';
export { applySkillTool } from './applySkill';
export { searchVaultTool } from './searchVault';
export { openVaultNoteTool } from './openVaultNote';
export { listSharePointDocsTool } from './listSharePointDocs';
export { proposeSharePointDocumentTool } from './proposeSharePointDocument';
export { openOnboardWizardTool } from './openOnboardWizard';
export { setFormFieldTool } from './setFormField';
export { inspectTenantTool } from './inspectTenant';
export { kickAdminConsentTool } from './kickAdminConsent';
export { submitWizardStepTool } from './submitWizardStep';
export { explainLoraTool } from './explainLora';
export { recommendCorpusTool } from './recommendCorpus';
export { openSpecializationLabTool } from './openSpecializationLab';
export { describeSpecializationControlsTool } from './describeSpecializationControls';
export { openWindowTool, NIPE_KNOWN_WINDOWS } from './openWindow';
export { closeWindowTool } from './closeWindow';
export { focusWindowTool } from './focusWindow';
export { minimizeWindowTool } from './minimizeWindow';
export { listOpenWindowsTool } from './listOpenWindows';
export { readWindowContextTool } from './readWindowContext';
export { readPromptSessionTool } from './readPromptSession';
export { setSystemPromptTool } from './setSystemPrompt';
export { setUserPromptTool } from './setUserPrompt';
export { addSkillPromptTool } from './addSkillPrompt';
export { removeSkillPromptTool } from './removeSkillPrompt';
export { updateSkillPromptTool } from './updateSkillPrompt';

/**
 * Combined context type. Every concrete runner context must satisfy
 * this — i.e. provide every callback referenced by every tool.
 *
 * Both `listSharePointDocs` and `proposeSharePointDocument` are
 * optional: some sessions (demo desktop without SP, fixture-only
 * vitest harness) have no SharePoint integration. When optional
 * callbacks are missing, the relevant tool surfaces a friendly
 * "integration not available" error instead of throwing.
 */
export interface NipeToolRunnerContext
  extends ListSkillsContext,
    GetSkillContext,
    ApplySkillContext,
    SearchVaultContext,
    OpenVaultNoteContext,
    ListSharePointDocsContext,
    ProposeSharePointDocumentContext,
    OpenOnboardWizardContext,
    SetFormFieldContext,
    InspectTenantContext,
    KickAdminConsentContext,
    SubmitWizardStepContext,
    ExplainLoraContext,
    RecommendCorpusContext,
    OpenSpecializationLabContext,
    DescribeSpecializationControlsContext,
    OpenWindowContext,
    CloseWindowContext,
    FocusWindowContext,
    MinimizeWindowContext,
    ListOpenWindowsContext,
    ReadWindowContextContext,
    ReadPromptSessionContext,
    SetSystemPromptContext,
    SetUserPromptContext,
    AddSkillPromptContext,
    RemoveSkillPromptContext,
    UpdateSkillPromptContext {}

/**
 * Registry of all NIPE tools. Keyed by tool name for dispatch.
 * Order in the array determines the order they appear in transport
 * payloads (kept stable for prompt-cache friendliness).
 */
export const ALL_TOOLS: ReadonlyArray<NipeTool<NipeToolRunnerContext>> = [
  listSkillsTool,
  getSkillTool,
  applySkillTool,
  searchVaultTool,
  openVaultNoteTool,
  listSharePointDocsTool,
  proposeSharePointDocumentTool,
  // F18 — customer onboarding wizard (5 tools)
  openOnboardWizardTool,
  setFormFieldTool,
  inspectTenantTool,
  kickAdminConsentTool,
  submitWizardStepTool,
  // L.4 — Specialization Lab discovery
  explainLoraTool,
  recommendCorpusTool,
  // L.15 — Specialization Lab navigation + Tinker control knowledge
  openSpecializationLabTool,
  describeSpecializationControlsTool,
  // Generic openWindow — catalogues every Repancy desktop window.
  openWindowTool,
  // F-NIPE-DESKTOP-AWARENESS — close/focus/minimize/list siblings.
  closeWindowTool,
  focusWindowTool,
  minimizeWindowTool,
  listOpenWindowsTool,
  // F-NIPE-DESKTOP-AWARENESS — per-window selection bridges.
  readWindowContextTool,
  // F-NIPE-PROMPT-LAB-DRIVER — Prompt Lab read + mutate.
  readPromptSessionTool,
  setSystemPromptTool,
  setUserPromptTool,
  // F-NIPE-PROMPT-LAB-SKILLS — skill-prompt CRUD.
  addSkillPromptTool,
  removeSkillPromptTool,
  updateSkillPromptTool,
] as unknown as ReadonlyArray<NipeTool<NipeToolRunnerContext>>;

/** Lookup table for the runner's dispatch. */
export const TOOLS_BY_NAME: ReadonlyMap<
  string,
  NipeTool<NipeToolRunnerContext>
> = new Map(ALL_TOOLS.map((t) => [t.schema.function.name, t]));

/** Schemas in transport order (for serialization to OpenAI tool format). */
export const ALL_TOOL_SCHEMAS: ReadonlyArray<NipeToolSchema> = ALL_TOOLS.map(
  (t) => t.schema,
);
